/**
 * Dynamic Table Component
 * =======================
 * 
 * Advanced table component for displaying form entries with inline filtering and sorting.
 * This component provides a comprehensive desktop interface for viewing and filtering
 * form submissions with real-time column filtering and multi-operation support.
 * 
 * Core Features:
 * - Dynamic column rendering based on form metadata
 * - Inline filtering with multiple operation types
 * - Column sorting with visual indicators
 * - Form type-specific filtering with dropdown selection
 * - Date and number field filtering with comparison operators
 * - Status field rendering with color-coded badges
 * - Responsive table design with horizontal scrolling
 * 
 * Filter Operations:
 * - Text fields: Equals, Contains
 * - Date/Number fields: Equals, Contains, Greater Than, Less Than
 * - Form Type: Dropdown selection with automatic conversion
 * - Real-time filter application with immediate feedback
 * 
 * Column Types:
 * - text: Standard text input with equals/contains operations
 * - date: Date input with comparison operations and formatting
 * - status: Color-coded badge display with predefined color mapping
 * - action: Clickable rows for navigation
 * - number: Numeric input with comparison operations
 * 
 * Technical Implementation:
 * - Input-driven column configuration
 * - Event-driven filtering and sorting
 * - Change detection optimization
 * - Local state management for filter operations
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { FormMetadata } from '../../../../../../models/form-registry';
import { ActionSheetController } from '@ionic/angular';

export interface TableColumn {
  key: string;
  label: string;
  type: 'text' | 'date' | 'status' | 'action' | 'number';
  sortable?: boolean;
  width?: string;
}

@Component({
  selector: 'app-dynamic-table',
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.css']
})
export class DynamicTableComponent {
  @Input() loadingState: { isLoading: boolean; message: string } = { isLoading: false, message: '' };
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  @Input() sortColumn: string = '';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';
  @Input() formTypes: FormMetadata[] = [];

  @Output() sort = new EventEmitter<{column: string, direction: 'asc' | 'desc', advancedFilter: boolean}>();
  @Output() action = new EventEmitter<any>();
  @Output() filter = new EventEmitter<{column: string, value: string, advancedFilter: boolean, operation: 'equals' | 'contains' | 'greaterThan' | 'lessThan'}>();
  @Output() clearFilter = new EventEmitter<void>();

  // Filter state
  activeFilterColumn: string | null = null;
  filterValues: { [key: string]: string } = {};
  filterOperations: { [key: string]: 'equals' | 'contains' | 'greaterThan' | 'lessThan' } = {};
  activeFilterKey: string | null = null; // Track which column has an active filter

  constructor(
    private actionSheetController: ActionSheetController,
    private cdr: ChangeDetectorRef
  ) {
  }

  /**
   * Lifecycle hook that responds to input property changes.
   * Handles data updates, form type changes, column changes, and sort state changes.
   * Manages filter state synchronization with sort column changes.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: any) {
    if (changes['loadingState']) {
      this.loadingState = changes['loadingState'].currentValue;
    }
    if (changes['data']) {
    }
    if (changes['formTypes']) {
    }
    if (changes['columns']) {
      // Debug formType column specifically
      const formTypeColumn = this.columns.find(col => col.key === 'formType');
    }
  
    if (changes['sortColumn']) {
      this.sortColumn = changes['sortColumn'].currentValue;
      this.activeFilterColumn = this.sortColumn;
      this.activeFilterKey = this.sortColumn;
      this.filterValues[this.sortColumn] = '';

    }
    if (changes['sortDirection']) {
    }
  }

  /**
   * Handles column sorting events.
   * Toggles sort direction and emits sort events to the parent component.
   * 
   * @param column - The column key to sort by
   */
  onSort(column: string) {
    const direction = this.sortColumn === column && this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.sort.emit({ column, direction, advancedFilter: false });
  }

  /**
   * Handles row action events (clicks).
   * Emits the row data to the parent component for navigation or other actions.
   * 
   * @param row - The row data that was clicked
   */
  onAction(row: any) {
    this.action.emit(row);
  }

  /**
   * Handles filter column click events.
   * Manages the active filter column state and clears previous filters when switching columns.
   * 
   * @param column - The column key that was clicked for filtering
   */
  onFilterClick(column: string) {
    // If clicking on the same column that already has an active filter, toggle it off
    if (this.activeFilterColumn === column) {
      this.activeFilterColumn = null;
    } else {
      // If clicking on a different column, clear the previous filter immediately
      if (this.activeFilterKey && this.activeFilterKey !== column) {
        this.clearPreviousFilter();
      }
      
      // If clicking on a different column, clear the previous filter input
      if (this.activeFilterColumn !== null) {
        this.activeFilterColumn = null;
      }
      this.activeFilterColumn = column;
    }
  }

  /**
   * Handles filter input value changes.
   * Stores the filter value for the specified column.
   * 
   * @param event - The input event containing the new value
   * @param column - The column key for the filter
   */
  onFilterInput(event: any, column: string) {
    const value = event.target.value;
    this.filterValues[column] = value;
  }

  /**
   * Applies a filter for a standard column.
   * Emits the filter event with the column, value, and operation.
   * 
   * @param column - The column key to filter by
   */
  onFilterApply(column: string) {
    const value = this.filterValues[column];
    const operation = this.filterOperations[column] || 'equals';
    if (value && value.trim()) {
      this.filter.emit({ column, value: value.trim(), advancedFilter: false, operation });
      this.activeFilterKey = column; // Keep track of which column has an active filter
    }
    this.activeFilterColumn = null; // Hide the input row
  }

  /**
   * Applies a filter for the form type column.
   * Converts hyphenated form types to underscore format for API compatibility.
   * 
   * @param event - The select change event containing the selected form type
   * @param column - The column key (should be 'formType')
   */
  onFilterApplyFormType(event: any, column: string) {
    const selectedFormType = event.detail.value;
    
    if (selectedFormType) {
      // Convert hyphenated form type to underscore format for API filtering
      const underscoreFormType = selectedFormType.replace(/-/g, '_');
      
      // Find the FormMetadata object that matches the selected formType
      const formMetadata = this.formTypes.find(type => type.formType === selectedFormType);
      
      if (formMetadata) {
        const operation = this.filterOperations[column] || 'equals';
        this.filter.emit({ column, value: underscoreFormType, advancedFilter: false, operation });
        this.activeFilterKey = column; // Keep track of which column has an active filter
        this.filterValues[column] = selectedFormType; // Store the selected value
      }
    }
    this.activeFilterColumn = null; // Hide the input row
  }

  /**
   * Clears the filter for a specific column.
   * Removes filter values and operations, and emits clear filter event.
   * 
   * @param column - The column key to clear filters for
   */
  onFilterClear(column: string) {
    delete this.filterValues[column];
    delete this.filterOperations[column];
    this.activeFilterColumn = null;
    this.activeFilterKey = null; // Clear the active filter tracking
    this.clearFilter.emit();
  }

  /**
   * Cancels the current filter operation.
   * Hides the filter input row without applying changes.
   */
  onFilterCancel() {
    this.activeFilterColumn = null;
  }

  /**
   * Presents an action sheet for selecting filter operations.
   * Shows different operations based on column type (text, date, number).
   * 
   * @param column - The column key to select operations for
   * @async
   */
  async onOperationSelect(column: string) {
    const columnType = this.getColumnType(column);
    const currentOperation = this.filterOperations[column] || 'equals';
    
    const buttons: any[] = [
      {
        text: 'Equals',
        handler: () => this.setOperation(column, 'equals')
      },
      {
        text: 'Contains',
        handler: () => this.setOperation(column, 'contains')
      }
    ];

    // Add date/number specific operations
    if (columnType === 'date' || columnType === 'number') {
      buttons.push(
        {
          text: 'Greater Than or Equal To',
          handler: () => this.setOperation(column, 'greaterThan')
        },
        {
          text: 'Less Than or Equal To',
          handler: () => this.setOperation(column, 'lessThan')
        }
      );
    }

    buttons.push({
      text: 'Cancel',
      role: 'cancel' as any
    });

    const actionSheet = await this.actionSheetController.create({
      header: 'Select Operation',
      buttons: buttons
    });

    await actionSheet.present();
  }

  /**
   * Sets the filter operation for a specific column.
   * Updates the operation and re-applies existing filters if present.
   * 
   * @param column - The column key to set the operation for
   * @param operation - The filter operation to set
   * @private
   */
  private setOperation(column: string, operation: 'equals' | 'contains' | 'greaterThan' | 'lessThan') {
    this.filterOperations[column] = operation;
    
    // Force change detection to update the UI
    this.cdr.detectChanges();
    
    // If there's already a filter value, re-apply the filter with the new operation
    if (this.filterValues[column] && this.filterValues[column].trim()) {
      this.filter.emit({ 
        column, 
        value: this.filterValues[column].trim(), 
        advancedFilter: false, 
        operation 
      });
    }
  }

  /**
   * Gets the display text for a filter operation.
   * Returns user-friendly text representations of filter operations.
   * 
   * @param column - The column key to get operation text for
   * @returns String representation of the filter operation
   */
  getOperationText(column: string): string {
    const operation = this.filterOperations[column] || 'equals';
    switch (operation) {
      case 'equals': return '=';
      case 'contains': return 'Contains';
      case 'greaterThan': return '>=';
      case 'lessThan': return '<=';
      default: return 'Equals';
    }
  }

  /**
   * Clears the previous filter when starting a new one.
   * Ensures only one column has an active filter at a time.
   * 
   * @private
   */
  private clearPreviousFilter() {
    if (this.activeFilterKey) {
      delete this.filterValues[this.activeFilterKey];
      this.activeFilterKey = null;
      this.clearFilter.emit();
    }
  }

  /**
   * Clears all local filters and sorting state.
   * Public method for external component access.
   */
  public onClearAllLocally() {
    this.clearFiltersLocally();
    this.clearSortLocally();
  }

  /**
   * Clears all local filter state.
   * Resets filter values, operations, and active filter tracking.
   */
  public clearFiltersLocally() {
    this.filterValues = {};
    this.filterOperations = {};
    this.activeFilterColumn = null;
    this.activeFilterKey = null;
  }

  /**
   * Clears local sort state.
   * Resets sort column and direction to defaults.
   */
  public clearSortLocally() {
    this.sortColumn = '';
    this.sortDirection = 'asc';
  }

  /**
   * Determines if a column should show the filter input row.
   * Shows filter input for active filter columns or columns with active filters.
   * 
   * @param column - The column key to check
   * @returns True if the filter input should be shown
   */
  shouldShowFilterInput(column: string): boolean {
    return this.activeFilterColumn === column || this.activeFilterKey === column;
  }

  /**
   * Gets the appropriate sort icon for a column.
   * Returns different icons based on current sort state and direction.
   * 
   * @param column - The column key to get sort icon for
   * @returns String name of the icon to display
   */
  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'swap-vertical-outline';
    return this.sortDirection === 'asc' ? 'arrow-up-outline' : 'arrow-down-outline';
  }

  /**
   * Gets the appropriate input type for filter inputs.
   * Returns 'date' for date columns, 'text' for others.
   * 
   * @param columnType - The column type to get input type for
   * @returns String input type for the filter input
   */
  getFilterInputType(columnType: string): string {
    switch (columnType) {
      case 'date':
        return 'date';
      default:
        return 'text';
    }
  }

  /**
   * Gets the placeholder text for filter inputs.
   * Returns format-specific placeholders for different column types.
   * 
   * @param columnType - The column type to get placeholder for
   * @returns String placeholder text for the filter input
   */
  getFilterPlaceholder(columnType: string): string {
    switch (columnType) {
      case 'date':
        return 'Search in YYYY-MM-DD';
      default:
        return 'Filter...';
    }
  }

  /**
   * Gets the color for status badges.
   * Maps status values to predefined Ionic colors for consistent styling.
   * 
   * @param status - The status value to get color for
   * @returns String color name for the status badge
   */
  getStatusColor(status: string): string {
    const statusMap: Record<string, string> = {
      'completed': 'success',
      'pending': 'warning',
      'rejected': 'danger',
      'draft': 'medium'
    };
    return statusMap[status.toLowerCase()] || 'medium';
  }

  /**
   * Track by function for ngFor optimization.
   * Uses item ID or index for efficient list rendering.
   * 
   * @param index - The index of the item
   * @param item - The item object
   * @returns Unique identifier for the item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  /**
   * Gets the column type for a specific column key.
   * Returns the type from the columns configuration or defaults to 'text'.
   * 
   * @param columnKey - The column key to get type for
   * @returns String column type
   */
  getColumnType(columnKey: string): string {
    const column = this.columns.find(col => col.key === columnKey);
    return column?.type || 'text';
  }
} 